import type PaymentApi from '../../services/payment/PaymentApi';
import {
  getAgent,
  getAssistedByAgent,
  getIIASPaymentDetails,
  getMetadata,
  getPartialAuthorization,
} from '../session/selectors';
import type { CheckoutSessionsResponse } from '../../services/commonCheckout/types/CheckoutSessionsResponse';
import { SUCCESSFUL_PAYMENT_SUBMISSION_STATUSES } from '../constants';
import noop from '../function/noop';
import type { SycurioPaymentMethodFormSubmitData } from '../../capabilities/sycurio-pci-forms/sycurio-submit-payment-view/SycurioSubmitPaymentView';
import createCardCategoriesArray from '../cardCategories/createCardCategoriesArray';
import type { MedicationOption } from '../../shared/types/CardCategories/MedicationOption';

const processSycurioPaymentSubmission = async ({
  /**
   * Processes Sycurio payment submission with medication and card categories support.
   * @param medicationOptions - List of medication options for manufacturer cards.
   * @param medication - Selected medication value.
   * @param onProcessingComplete - Callback after processing completes.
   * @param onSuccess - Callback on successful payment.
   */
  nameOnCard: name,
  isDefault,
  manufacturerCard: isManufacturerCard,
  nickname,
  savePaymentMethod,
  paymentMethodId,
  last4,
  originalCheckoutSessionResponse,
  paymentDescription,
  amount,
  merchantTransactionId,
  authorizeCard,
  statementDescriptorSuffix,
  paymentApi,
  medicationOptions = [],
  medication,

  onProcessingComplete = noop,
  onSuccess = noop,
}: SycurioPaymentMethodFormSubmitData & {
  originalCheckoutSessionResponse: CheckoutSessionsResponse;
  paymentDescription: string;
  amount: number;
  merchantTransactionId: string;
  authorizeCard?: boolean;
  statementDescriptorSuffix?: string;
  paymentApi: typeof PaymentApi;
  medicationOptions?: MedicationOption[];
  medication?: string;
  onProcessingComplete?: () => void;
  onSuccess?: () => void;
}) => {
  const agent = getAgent(originalCheckoutSessionResponse);
  const assistedByAgent = getAssistedByAgent(
    originalCheckoutSessionResponse,
  );
  if (paymentMethodId) {
    try {
      const paymentResponse = await paymentApi.payAndStore({
        authRequired: true,
        paymentMethod: {
          type: 'CARD',
          paymentMethodDetails: {
            type: 'CARD',
            manufacturerCard: isManufacturerCard,
            cardCategories: createCardCategoriesArray({
              manufacturerCardTypeOptions: {
                value: medication,
                medications: medicationOptions,
              },
            }),
            last4,
          },
          nameOnCard: name,
          vendorPaymentMethodId: paymentMethodId,
          vendor: 'STRIPE',
          savePaymentMethod,
          nickname,
          default: isDefault,
          sourceProvider: {
            name: 'CCG',
          },
          paymentMethodSourceDetails: {
            channel: 'TELEPHONIC_ENTRY',
            uiSourceFragment: 'SYCURIO',
          },
        },
        agent,
        assistedByAgent,
        paymentDescription,
        paymentDetails: getIIASPaymentDetails(
          originalCheckoutSessionResponse,
        ),
        amount,
        merchantTransactionId,
        authorizeCard,
        partialAuthorization: getPartialAuthorization(
          originalCheckoutSessionResponse,
        ),
        statementDescriptorSuffix,
        metadata: getMetadata(originalCheckoutSessionResponse),
      });

      if (!paymentResponse) {
        throw new Error('paymentResponse unavailable');
      }

      if (
        paymentResponse &&
        SUCCESSFUL_PAYMENT_SUBMISSION_STATUSES.includes(
          paymentResponse?.data.status,
        )
      ) {
        onSuccess();
      }
    } finally {
      onProcessingComplete();
    }
  }
};

export default processSycurioPaymentSubmission;
